<script setup>
import { ref, computed, onBeforeUnmount, watch }    from 'vue'
import { useTranslaterStore }                       from '@/stores/translater'
import { useUserCarsStore }                         from '@/stores/user-cars'
import UTAUserCarsCreate                            from '@/components/user/UTAUserCarsCreate.vue'
import UTACarImage                                  from '@/components/car/UTACarImage.vue'
import Modal                                        from '@/components/UTAModal.vue'
import vinDecoder                                   from '@/helpers/vinDecoder'

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const translaterStore   = useTranslaterStore()
const userCarsStore     = useUserCarsStore()

const getTranslate          = computed(() => translaterStore.getTranslate)
const userCars              = computed(() => userCarsStore.userCars)
const userCarsProcess       = computed(() => userCarsStore.userCarsProcess)
const userCarsMsg           = computed(() => userCarsStore.userCarsMsg)
const userCarsCreateMsg     = computed(() => userCarsStore.userCarsCreateMsg)
const userCarsDeleteProcess = computed(() => userCarsStore.userCarsDeleteProcess)
const userCarsDeleteMsg     = computed(() => userCarsStore.userCarsDeleteMsg)

const viewMode              = ref('')
const inputSearch           = ref('')
const pageNumber            = ref(1)
const deleteUserCarsUuid    = ref(null)
const showCreateUserCars    = ref(false)
const showDeleteUserCars    = ref(false)
const searchMode            = ref(null)
const searchModeText        = ref('CARS_SEARCH_MODE_ALL')

function changePage(cursor, dir) {

    const params = {
        uuid_user: props.uuid, 
        'c.limit': 12, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    userCarsStore.readUserCars(params).then(result => {
        if(result.array.length) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

function setViewMode(mode) {

    viewMode.value = mode
    localStorage.setItem('UTA_USER_CARS_VIEW', mode)
}

function setSearchMode(mode) {

    if(mode) {

        searchMode.value = mode

        switch(mode) {

            case 'vin': 
                searchModeText.value = 'CARS_SEARCH_MODE_VIN'
                break

            case 'tid': 
                searchModeText.value = 'CARS_SEARCH_MODE_TID'
                break

            case 'imei': 
                searchModeText.value = 'CARS_SEARCH_MODE_IMEI'
                break

            case 'car_number': 
                searchModeText.value = 'CARS_SEARCH_MODE_NUMBER'
                break

            case 'nami_number': 
                searchModeText.value = 'CARS_SEARCH_MODE_NAMI'
                break

            case 'phone': 
                searchModeText.value = 'CARS_SEARCH_MODE_PHONE'
                break

            case 'iccid': 
                searchModeText.value = 'CARS_SEARCH_MODE_ICCID'
                break
        }

    } else {

        searchMode.value = null
        searchModeText.value = 'CARS_SEARCH_MODE_ALL'
    }

    let params = {
        'c.limit': 12,
        user_array: props.uuid
    }

    if(inputSearch.value.length > 2)
        params['c.q'] = inputSearch.value

    if(searchMode.value) 
        params['c.filterQ'] = searchMode.value

    userCarsStore.readUserCars(params)
}

userCarsStore.readUserCars({
    uuid_user: props.uuid, 
    'c.limit': 12
})
viewMode.value = localStorage.getItem('UTA_USER_CARS_VIEW') || 'grid'

onBeforeUnmount(() => {
    userCarsStore.setUserCarsMsg({})
    userCarsStore.setUserCarsCreateMsg({})
    userCarsStore.setUserCarsDeleteMsg({})
})

watch(userCarsCreateMsg, msg => {
    if('success' in msg && msg.success) {
        userCarsStore.readUserCars({ uuid_user: props.uuid, 'c.limit': 12 })
    }
})

watch(userCarsDeleteMsg, msg => {
    if('success' in msg && msg.success) {
        showDeleteUserCars.value = false
        userCarsStore.readUserCars({ uuid_user: props.uuid, 'c.limit': 12 })
    }
})

watch(inputSearch, input => {
    let params = {
        uuid_user: props.uuid, 
        'c.limit': 12
    }
    if(input.length > 2) {
        params['c.q'] = input
        if(searchMode.value) {
            params['c.filterQ'] = searchMode.value
        }
    }
    userCarsStore.readUserCars(params)
})
</script>

<template>
    <div class="uta-cars-group-cars pb-5">

        <header class="uta-page-header" role="heading" aria-level="1">
            <h1 class="uta-page-header__title">{{ getTranslate.USER_CARS }}</h1>
            <div class="uta-page-header__actions">
                <button 
                    class="uta-page-header__action uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon" 
                    type="button"
                    @click.prevent="showCreateUserCars = true" 
                >
                    <span class="uta-btn__content">
                        <span class="uta-btn__icon material-icons">settings</span>
                        <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_CTRL }}</span>
                    </span>
                </button>
            </div>
        </header>
        
        <div class="row mb-4 justify-content-between">
            <div class="col-12 col-md-8 col-lg-10">
                <div class="input-group uta-input__wrapper uta-search">
                    <button class="btn btn-outline-secondary dropdown-toggle uta-search__filter-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        {{ getTranslate[searchModeText] }}
                    </button>
                    <ul class="dropdown-menu uta-search__filter-menu">
                        <li class="uta-search__filter-menu-item">
                            <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('')">{{ getTranslate.CARS_SEARCH_MODE_ALL }}</a>
                        </li>
                        <li  class="uta-search__filter-menu-item">
                            <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('vin')">{{ getTranslate.CARS_SEARCH_MODE_VIN }}</a>
                        </li>
                        <li  class="uta-search__filter-menu-item">
                            <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('imei')">{{ getTranslate.CARS_SEARCH_MODE_IMEI }}</a>
                        </li>
                        <li  class="uta-search__filter-menu-item">
                            <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('car_number')">{{ getTranslate.CARS_SEARCH_MODE_NUMBER }}</a>
                        </li>
                        <li  class="uta-search__filter-menu-item">
                            <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('phone')">{{ getTranslate.CARS_SEARCH_MODE_PHONE }}</a>
                        </li>
                        <li  class="uta-search__filter-menu-item">
                            <a class="dropdown-item uta-search__filter-menu-item-link" href="#" @click.prevent="setSearchMode('iccid')">{{ getTranslate.CARS_SEARCH_MODE_ICCID }}</a>
                        </li>
                    </ul>
                    <input 
                        type="text" 
                        class="form-control uta-input__wrapper-input uta-search__input"
                        :placeholder="getTranslate.SEARCH + '...'" 
                        v-model="inputSearch"
                        @keyup.escape="inputSearch = ''"
                    >
                    <template v-if="inputSearch.length > 2">
                        <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                            cancel
                        </span>
                    </template>
                    <template v-else>
                        <span class="material-icons uta-input__wrapper-icon">
                            search
                        </span>
                    </template>
                </div>
            </div>
            <div class="d-none d-md-flex view-selector">
                <a href="#" class="view-selector__item view-selector__item--lines" @click.prevent="setViewMode('lines')"></a>
                <a href="#" class="view-selector__item view-selector__item--grid" @click.prevent="setViewMode('grid')"></a>
            </div>
        </div>

        <div 
            class="mb-4 uta-alert uta-alert--danger" 
            v-if="'success' in userCarsMsg && !userCarsMsg.success"
        >
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate[userCarsMsg.text] || userCarsMsg.text }}
            </div>
        </div>

        <div 
            class="uta-alert uta-alert--danger mb-4" 
            :class="{
                'uta-alert--success' : userCarsDeleteMsg.success, 
                'uta-alert--danger' : !userCarsDeleteMsg.success
            }"
            v-if="'success' in userCarsDeleteMsg"
        >
            <span class="material-icons uta-alert__icon">
                {{ userCarsDeleteMsg.success ? 'check_circle_outline' : 'error_outline' }}
            </span>
            <div class="uta-alert__text">
                {{ getTranslate[userCarsDeleteMsg.text] || userCarsDeleteMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="userCarsStore.setUserCarsDeleteMsg({})">
                <div class="material-icons">clear</div>
            </button>
        </div>

        <template v-if="userCars.array?.length">

            <template v-if="viewMode == 'lines'">
                <div class="container-fluid">
                    <div class="uta-cars__list uta-list">
                        <div class="uta-list__title d-none d-lg-block uta-cars__title">
                            <div class="row">
                                <div class="col-10 col-xl-11">
                                    <div class="row">
                                        <div class="col-12 col-lg-4">{{ getTranslate.CARS_IMEI }}</div>
                                        <div class="col-12 col-lg-4">{{ getTranslate.CARS_VIN }}</div>
                                        <div class="col-12 col-lg-4">{{ getTranslate.CARS_MODEL }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="uta-list__content">
                            <div 
                                class="row align-items-center uta-list__item uta-cars__car uta-cars__car--lines" 
                                :class="{ 'uta-list__item--dark' : index % 2 }" 
                                v-for="(car, index) in userCars.array" 
                                :key="car.uuid"
                            >
                                <div class="col-10 col-xl-11">
                                    <div class="row">
                                        <div class="col-12 col-lg-4 uta-cars__car-vin">
                                            <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_IMEI }}: </span>
                                            <router-link :to="'/cars/' + car.uuid" class="uta-list__item-link uta-cars__link">{{ car.data?.imei || '&mdash;' }}</router-link>
                                        </div>
                                        <div class="col-12 col-lg-4 uta-cars__car-model">
                                            <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_VIN }}: </span>
                                            {{ car.data?.vin || '&mdash;' }}
                                        </div>
                                        <div class="col-12 col-lg-4 uta-cars__car-name">
                                            <span class="d-inline d-lg-none uta-cars__car-mobile-title">{{ getTranslate.CARS_MODEL }}: </span>
                                            {{ vinDecoder(car.data?.vin).model || '&mdash;' }}
                                        </div>
                                    </div>
                                </div>
                                <div class="col-2 col-xl-1 text-end">
                                    <div
                                        class="material-icons uta-list__item-icon "
                                        :title="getTranslate.CAR_GROUPS_CARS_DELETE"
                                        @click="
                                            deleteUserCarsUuid = car.uuid, 
                                            showDeleteUserCars = true
                                        "
                                    >
                                        delete
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template v-if="viewMode == 'grid'">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3" v-for="(car, index) in userCars.array" :key="index">
                        <div class="uta-cars__car uta-cars__car--grid">
                            <router-link :to="'/cars/' + car.uuid" class="uta-cars__car-image">
                                <UTACarImage :vin="car.data?.vin" />
                            </router-link>
                            <div 
                                class="uta-cars__car-delete" 
                                @click="
                                    deleteUserCarsUuid = car.uuid, 
                                    showDeleteUserCars = true
                                "
                            >
                                <span class="material-icons">
                                    delete
                                </span>
                            </div>
                            <div class="uta-cars__car-vin">
                                <template v-if="car.data.vin">
                                    {{ car.data.vin }}
                                </template>
                                <template v-else>
                                    <span class="opacity-25">{{ getTranslate.CARS_VIN_EMPTY }}</span>
                                </template>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <div class="uta-cars__car-imei">
                                        {{ getTranslate.CARS_IMEI }}: 
                                        {{ car.data?.imei || '&mdash;' }}
                                    </div>
                                </div>
                                <div class="col-12">
                                    <div class="uta-cars__car-model">
                                        {{ getTranslate.CARS_MODEL }}: 
                                        {{ vinDecoder(car.data?.vin).model || '&mdash;' }}
                                    </div>
                                </div>
                            </div>
                            <div class="row mt-4">
                                <div class="col-12 col-md-6">
                                    <div class="uta-cars__car-status">
                                        <span class="uta-cars__car-status-icon"></span>
                                        <span class="uta-cars__car-status-text">
                                            {{ getTranslate.CAR_STATUS_OFFLINE }}
                                        </span>
                                    </div>
                                </div>
                                <div class="col-12 col-md-6 d-flex justify-content-end">
                                    <router-link :to="'/cars/' + car.uuid" class="uta-cars__car-readmore">
                                        {{ getTranslate.CARS_READMORE }} 
                                        <span class="material-icons">
                                            navigate_next
                                        </span>
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <div class="my-5 uta-cars-group-cars__pagination" v-if="userCars.c && (userCars.c.isBefore || userCars.c.isAfter)">
                <div class="row">
                    <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3">
                        <div class="uta-pagination">
                            <button 
                                :disabled="userCarsProcess || !userCars.c.isBefore"
                                class="uta-btn uta-btn--sm uta-pagination__prev" 
                                @click.prevent="changePage(userCars.c.start, 'before')"
                            >
                                <span class="material-icons">
                                    navigate_before
                                </span>
                            </button>
                            <div class="uta-pagination__current">
                                {{ pageNumber }}
                            </div>
                            <button 
                                :disabled="userCarsProcess || !userCars.c.isAfter"
                                class="uta-btn uta-btn--sm uta-pagination__next" 
                                @click.prevent="changePage(userCars.c.stop, 'after')"
                            >
                                <span class="material-icons">
                                    navigate_next
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <transition name="bounce">
            <Modal 
                v-if="showCreateUserCars"
                :header="getTranslate.USER_CARS_CREATE"
                size="lg"
                @closeModal="showCreateUserCars = false"
            >
                <template v-slot:body>
                    <UTAUserCarsCreate @closeModal="showCreateUserCars = false" :uuid="props.uuid" />
                </template>
            </Modal>
        </transition>

        <transition name="bounce">
            <Modal 
                v-if="showDeleteUserCars"
                :header="getTranslate.USER_CARS_DELETE"
                size="md"
                @closeModal="showDeleteUserCars = false"
            >
                <template v-slot:body>
                    <form action="" @submit.prevent="userCarsStore.deleteUserCars({ car: deleteUserCarsUuid, user: props.uuid })">
                        {{ getTranslate.USER_CARS_DELETE_DESCR }}
                        <div class="mt-5 uta-btns">
                            <button type="button" class="uta-btn uta-btn" @click="showDeleteUserCars = false">{{ getTranslate.BTN_CANCEL }}</button>
                            <button type="submit" class="uta-btn uta-btn--main" :disabled="userCarsDeleteProcess">{{ getTranslate.BTN_DELETE }}</button>
                        </div>
                    </form>
                </template>
            </Modal>
        </transition>

    </div>
</template>


<style lang="scss">
@import '@/assets/styles/scss/components/cars';
</style>